import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DialogRef } from '@angular/cdk/dialog';
import {
  form,
  FormField,
  maxLength,
  minLength,
  required,
} from '@angular/forms/signals';
import {
  AppSvgIconComponent,
  BaseOverlayComponent,
  SignalButton,
  SignalFormDirective,
  SignalTextField,
  State,
  validateSignalForm,
} from '@vallift/ngl';
import { FormsModule } from '@angular/forms';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { CreateFormApiResponse } from '@core/api/workspace/models/createFormApiResponse';
import type { CreateFormRequest } from '@core/api/workspace/models/createFormRequest';

export type CreateFormStep = 'name' | 'ai' | 'template';

export interface CreateFormOverlayResult {
  id: string;
  name: string;
  type: string;
  slug: string;
  entry: 'ai' | 'scratch';
  initialPrompt?: string;
}

interface CreateFormNameModel {
  name: string;
}

const DEFAULT_FORM_TYPE = 'Other';

@Component({
  selector: 'app-create-form-overlay',
  standalone: true,
  imports: [
    AppSvgIconComponent,
    BaseOverlayComponent,
    FormField,
    FormsModule,
    SignalButton,
    SignalFormDirective,
    SignalTextField,
  ],
  templateUrl: './create-form-overlay.html',
  styleUrl: './create-form-overlay.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateFormOverlayComponent extends BaseComponent {
  private readonly userAPIService = inject(WorkspaceAPIService);
  private readonly dialogRef = inject(DialogRef<CreateFormOverlayResult | null>);

  readonly createState = new State<CreateFormApiResponse>();
  readonly step = signal<CreateFormStep>('name');
  readonly prompt = signal('');

  readonly model = signal<CreateFormNameModel>({ name: '' });

  readonly nameForm = form(this.model, (schemaPath) => {
    required(schemaPath.name, { message: 'This field is required' });
    minLength(schemaPath.name, 1, { message: 'This field is required' });
    maxLength(schemaPath.name, 200, { message: 'Maximum 200 characters allowed' });
  });

  readonly overlayTitle = computed(() => {
    switch (this.step()) {
      case 'ai':
        return 'Describe your form';
      case 'template':
        return 'Choose a template';
      default:
        return 'New form';
    }
  });

  readonly overlayDescription = computed(() => {
    switch (this.step()) {
      case 'ai':
        return 'Start with a prompt, or create from scratch.';
      case 'template':
        return 'Template gallery is on the way.';
      default:
        return 'Give your form a name to get started.';
    }
  });

  readonly showBackButton = computed(
    () => this.step() === 'ai' || this.step() === 'template',
  );

  readonly canContinueName = computed(() => {
    if (this.createState.loading()) {
      return false;
    }
    return this.model().name.trim().length >= 1;
  });

  readonly canGenerate = computed(() => {
    if (this.createState.loading()) {
      return false;
    }
    return this.prompt().trim().length >= 1;
  });

  close(): void {
    this.dialogRef.close(null);
  }

  onBack(): void {
    if (this.step() === 'template') {
      this.step.set('ai');
      return;
    }
    if (this.step() === 'ai') {
      this.step.set('name');
    }
  }

  goToAiStep(): void {
    if (!this.canContinueName() || !validateSignalForm(this.nameForm)) {
      return;
    }
    this.step.set('ai');
  }

  goToTemplateStep(): void {
    this.step.set('template');
  }

  onGenerate(): void {
    const text = this.prompt().trim();
    if (!text || !this.canGenerate()) {
      return;
    }
    this.createAndClose('ai', text);
  }

  onPromptKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onGenerate();
    }
  }

  onCreateFromScratch(): void {
    this.createAndClose('scratch');
  }

  private createAndClose(entry: 'ai' | 'scratch', initialPrompt?: string): void {
    if (this.createState.loading()) {
      return;
    }

    const name = this.model().name.trim();
    if (!name) {
      this.step.set('name');
      return;
    }

    const request: CreateFormRequest = {
      name,
      type: DEFAULT_FORM_TYPE,
    };

    this.executeRequest({
      state: this.createState,
      request: this.userAPIService.createForm(request),
      showLoader: true,
      handleError: true,
      onSuccess: (response) => {
        this.dialogRef.close({
          id: response.data.id,
          name: response.data.name,
          type: response.data.type,
          slug: response.data.slug,
          entry,
          ...(initialPrompt?.trim()
            ? { initialPrompt: initialPrompt.trim() }
            : {}),
        });
      },
    });
  }
}
